import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';

import { getBackendUrl } from '../../lib/runtimeConfig';

const API = `${getBackendUrl()}/api`;

// Haversine distance in miles between two lat/lon pairs. Used for outlier
// rejection — when a geocoded stop is hundreds of miles from the cluster
// centroid, the geocoder almost certainly hit a wrong-country namesake
// (e.g. Rutherford, ON vs Rutherford, CA).
function milesBetween(a, b) {
  const R = 3958.8;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const sa =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(sa), Math.sqrt(1 - sa));
  return R * c;
}

// Drop any geocoded points >250 miles from the median of the others. With 3
// stops we expect them all to be in the same wine region; a wild outlier is
// almost always a geocoder hijack (different country, same street name).
function rejectOutliers(points) {
  const valid = points.filter((p) => typeof p?.lat === 'number' && typeof p?.lon === 'number');
  if (valid.length < 2) return points;
  return points.map((p) => {
    if (typeof p?.lat !== 'number' || typeof p?.lon !== 'number') return p;
    // Compare against the median of the OTHER points.
    const others = valid.filter((o) => o !== p);
    if (others.length === 0) return p;
    const dists = others.map((o) => milesBetween(p, o));
    const median = dists.sort((a, b) => a - b)[Math.floor(dists.length / 2)];
    if (median > 250) {
      // Strip the bad coords so the polyline/markers ignore this stop.
      const { lat: _lat, lon: _lon, ...rest } = p;
      return rest;
    }
    return p;
  });
}

// Reorder stops by minimum total path length so the user never gets a
// "north then south then back north" zig-zag — even when the LLM returns
// stops in a sub-optimal order. With 3 stops there are only 6 orderings and
// 3 distinct undirected paths; we brute-force all and pick the shortest.
//
// `items` may contain stops without lat/lon (still pending geocoding) — in
// that case we leave the order untouched. We only reorder when EVERY stop
// has resolved coords, otherwise renumbering mid-stream causes flicker.
function reorderByShortestPath(items) {
  if (!Array.isArray(items) || items.length < 3) return items;
  if (!items.every((p) => typeof p?.lat === 'number' && typeof p?.lon === 'number')) return items;

  // Pre-compute pairwise haversine distances.
  const n = items.length;
  const d = Array.from({ length: n }, () => Array(n).fill(0));
  for (let i = 0; i < n; i += 1) {
    for (let j = i + 1; j < n; j += 1) {
      const dist = milesBetween(items[i], items[j]);
      d[i][j] = dist;
      d[j][i] = dist;
    }
  }

  // Permutations of 0..n-1 (small n — n=3 → 6 perms).
  const permute = (arr) => {
    if (arr.length <= 1) return [arr];
    const out = [];
    for (let i = 0; i < arr.length; i += 1) {
      const rest = [...arr.slice(0, i), ...arr.slice(i + 1)];
      for (const tail of permute(rest)) out.push([arr[i], ...tail]);
    }
    return out;
  };

  const perms = permute(items.map((_, idx) => idx));
  let bestPerm = perms[0];
  let bestTotal = Infinity;
  for (const p of perms) {
    let total = 0;
    for (let i = 0; i < p.length - 1; i += 1) total += d[p[i]][p[i + 1]];
    if (total < bestTotal) {
      bestTotal = total;
      bestPerm = p;
    }
  }
  // If the model's ordering is already optimal, return the same reference
  // so React skips re-renders.
  const alreadyOptimal = bestPerm.every((v, i) => v === i);
  if (alreadyOptimal) return items;
  return bestPerm.map((idx) => items[idx]);
}

// Server-side proxy to Nominatim — avoids the CORS block that nominatim.org
// applies to browser-origin fetches and lets the backend send a compliant
// User-Agent. Single best result; null on miss.
async function geocodeAddress(address, name) {
  try {
    const params = new URLSearchParams({ q: address });
    if (name) params.set('name', name);
    const url = `${API}/ai-search/geocode?${params.toString()}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const data = await res.json();
    if (typeof data?.lat === 'number' && typeof data?.lon === 'number') {
      return { lat: data.lat, lon: data.lon };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * sessionStorage key used to persist the live AI search session across
 * route changes. Critical for the "click Add tour → SignIn → Back"
 * flow: without this the user's freshly-generated itinerary would
 * disappear because HeroSection unmounts when React Router navigates
 * away. We keep this in sessionStorage (not localStorage) so the
 * session naturally clears when the user closes the tab.
 */
const SESSION_KEY = 'winery-finder-ai-search-session-v1';

/**
 * Lazy initialiser — reads any persisted state from sessionStorage on
 * mount. Returns a partial state object with whichever fields were
 * stashed; missing fields fall back to `defaults`. Never throws — a
 * malformed blob just resets the session.
 */
function loadPersistedSession() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') return null;
    // Stale-check: drop any session older than 30 min. The user came
    // back to a stale tab — better to start fresh than show ghost data.
    if (data.savedAt && Date.now() - data.savedAt > 30 * 60 * 1000) {
      window.sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function persistSession(state) {
  if (typeof window === 'undefined') return;
  try {
    // Only persist when there's actually something to restore. An empty
    // session blob just wastes a sessionStorage slot.
    if (!state.submittedQuery && (!state.results || state.results.length === 0)) {
      window.sessionStorage.removeItem(SESSION_KEY);
      return;
    }
    window.sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ ...state, savedAt: Date.now() }),
    );
  } catch {
    // sessionStorage can be disabled / full — fail silently. The
    // session won't survive a navigation but the rest of the app
    // continues to work.
  }
}

/**
 * Centralised state + behaviours for the AI itinerary planner.
 *
 * Returns:
 *   - query / setQuery: controlled input value
 *   - submittedQuery: the query last sent to the model
 *   - isSearching: in-flight loading flag
 *   - results: ordered itinerary cards (with lat/lon patched in as geocoding resolves)
 *   - rejectedQuery: last query that hit OUT_OF_SCOPE (drives the recovery panel)
 *   - activeChatHistory: [{role,content}] sliding window for multi-turn refinement
 *   - turnCount: 1-based current turn number
 *   - searchActive: convenience flag — true while loading OR results are showing
 *   - runSearch(raw, historyOverride?): execute a new search turn
 *   - handleResetChat(): wipe everything back to the empty initial state
 *
 * State is mirrored to `sessionStorage` so the user can navigate away
 * (e.g. tap "Add" on the Audio Tour upsell, sign in, hit Back) and
 * come back to the exact same conversation. See SESSION_KEY above.
 */
export default function useAiSearch() {
  // Lazy-init from sessionStorage so a fresh mount after navigation
  // restores the previous conversation. Falls back to defaults when
  // nothing is persisted (first visit, tab close, or stale session).
  const initial = loadPersistedSession() || {};
  const [query, setQuery] = useState(initial.query || '');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState(initial.results || []);
  const [commentary, setCommentary] = useState(initial.commentary || '');
  const [followUps, setFollowUps] = useState(initial.followUps || []);
  const [matchedWinery, setMatchedWinery] = useState(initial.matchedWinery || null);
  const [submittedQuery, setSubmittedQuery] = useState(initial.submittedQuery || '');
  const [rejectedQuery, setRejectedQuery] = useState(null);
  // Clarification payload returned by the backend qualifier when the
  // user's query is too vague to plan against ("picnic deli", "wheelchair
  // accessible"). Shape: { headline, picks: [{label, expand, icon}],
  // regions: [{label, value}], original_query }. When set, the results
  // pane renders a clarification card instead of an itinerary.
  const [clarification, setClarification] = useState(null);
  const [activeChatHistory, setActiveChatHistory] = useState(initial.activeChatHistory || []);
  const [maxRadiusMiles, setMaxRadiusMiles] = useState(
    typeof initial.maxRadiusMiles === 'number' ? initial.maxRadiusMiles : 20,
  ); // default: 20 mi — matches RADIUS_STEPS so slider snaps cleanly on first render
  const [radiusSpread, setRadiusSpread] = useState(initial.radiusSpread || null); // { miles, limit, status: 'ok' | 'over' } | null
  const [route, setRoute] = useState(null); // recomputed on mount from geocoded stops, no need to persist
  const [routeError, setRouteError] = useState(false);
  const routeKeyRef = useRef('');
  const abortRef = useRef(false);

  const searchActive = isSearching || results.length > 0 || Boolean(clarification);
  const completedTurns = Math.floor(activeChatHistory.length / 2);
  // While loading we're computing the NEXT turn; otherwise we're displaying the latest completed one.
  const turnCount = isSearching ? completedTurns + 1 : Math.max(completedTurns, 1);

  const runSearch = async (rawQuery, historyOverride) => {
    const q = (rawQuery || '').trim().slice(0, 300);
    if (q.length < 3) {
      toast.error('Type a few more words about the vibe you want.');
      return;
    }

    // Soft turn cap — block the 7th+ turn. The cap is enforced here (not
    // just in the form UI) so even programmatic callers (clarification chips,
    // follow-up grid) respect it. Context IS preserved for all 6 turns because
    // MAX_HISTORY_MESSAGES on the backend was raised to 12.
    const currentCompletedTurns = Math.floor(activeChatHistory.length / 2);
    if (currentCompletedTurns >= 6) {
      toast.info("You've refined this trip 6 times — start fresh to plan a new adventure!");
      return;
    }

    setSubmittedQuery(q);
    setIsSearching(true);
    setResults([]);
    setCommentary('');
    setFollowUps([]);
    setRejectedQuery(null);
    setClarification(null);
    setRadiusSpread(null);
    abortRef.current = false;

    try {
      const historySnapshot = Array.isArray(historyOverride) ? historyOverride : activeChatHistory;
      const res = await axios.post(
        `${API}/ai-search`,
        {
          query: q,
          chat_history: historySnapshot,
          max_radius_miles: maxRadiusMiles || null,
        },
        { timeout: 45000 },
      );

      if (res.data?.error === 'OUT_OF_SCOPE_QUERY' || res.data?.error === 'INVALID_QUERY') {
        setIsSearching(false);
        setRejectedQuery(q);
        toast.error(
          'Winery Finder specializes in custom wine-country itineraries, lodging, and fine dining pairings. Please refine your query to a wine region or weekend travel vibe!',
        );
        return;
      }

      // Clarification interceptor: backend qualifier deflected a vague
      // query ("picnic deli", "wheelchair accessible") and returned
      // quick-pick chips instead of an itinerary. Render the card and
      // bail before the itinerary-parsing path.
      if (res.data?.type === 'clarification') {
        setIsSearching(false);
        setClarification(res.data);
        return;
      }

      const itinerary = Array.isArray(res.data?.itinerary) ? res.data.itinerary : [];
      const newCommentary = (res.data?.commentary || '').trim();
      const newFollowUps = Array.isArray(res.data?.follow_up_suggestions)
        ? res.data.follow_up_suggestions.filter((s) => typeof s === 'string' && s.trim().length > 0)
        : [];
      const newMatchedWinery = typeof res.data?.matched_winery === 'string' && res.data.matched_winery.trim()
        ? res.data.matched_winery.trim()
        : null;

      // If the backend fuzzy-matched a real winery from the user's query
      // (e.g. they typed "Castle Winery" → matched "Castello di Amorosa"),
      // pin that stop as #1. This is the "you searched, here it is FIRST"
      // promise — the badge in AiSearchResults makes the link explicit.
      const orderedItinerary = (() => {
        if (!newMatchedWinery || itinerary.length < 2) return itinerary;
        const target = newMatchedWinery.toLowerCase();
        const idx = itinerary.findIndex((stop) => {
          const n = (stop?.name || '').toLowerCase();
          return n.includes(target) || target.includes(n);
        });
        if (idx <= 0) return itinerary;
        const reordered = [...itinerary];
        const [hit] = reordered.splice(idx, 1);
        reordered.unshift(hit);
        return reordered;
      })();

      if (itinerary.length === 0) {
        setIsSearching(false);
        toast.error('No matches came back — try a different vibe.');
        return;
      }

      setResults(orderedItinerary);
      setCommentary(newCommentary);
      setFollowUps(newFollowUps);
      setMatchedWinery(newMatchedWinery);
      setRoute(null);
      setRouteError(false);
      routeKeyRef.current = '';
      setIsSearching(false);

      setActiveChatHistory([
        ...historySnapshot,
        { role: 'user', content: q },
        { role: 'assistant', content: JSON.stringify(itinerary) },
      ]);

      // Sequential geocoding (Nominatim 1 req/sec limit). Patch coords in as each resolves.
      for (let i = 0; i < itinerary.length; i += 1) {
        if (abortRef.current) break;
        // eslint-disable-next-line no-await-in-loop
        const coords = await geocodeAddress(itinerary[i].address, itinerary[i].name);
        if (abortRef.current) break;
        if (coords) {
          setResults((prev) => {
            const next = [...prev];
            if (next[i]) next[i] = { ...next[i], ...coords };
            return next;
          });
        }
        if (i < itinerary.length - 1) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 1100));
        }
      }

      // All geocodes have settled — reorder by shortest total path so the user
      // never gets a north-south-north zig-zag (the LLM's ordering doesn't
      // always reflect optimal spatial sequencing).
      if (!abortRef.current) {
        setResults((prev) => {
          const reordered = reorderByShortestPath(prev);

          // Post-validation: if the user set a hard radius, verify every pair
          // of stops is actually inside it. Even with the strengthened prompt,
          // Claude occasionally cheats on tight radii — surface a transparent
          // warning so the user knows + can re-run with a looser constraint.
          if (maxRadiusMiles && reordered.length >= 2) {
            const coords = reordered.filter(
              (s) => typeof s?.lat === 'number' && typeof s?.lon === 'number',
            );
            let maxPair = 0;
            for (let i = 0; i < coords.length - 1; i += 1) {
              for (let j = i + 1; j < coords.length; j += 1) {
                const d = milesBetween(coords[i], coords[j]);
                if (d > maxPair) maxPair = d;
              }
            }
            // Allow a small 15% tolerance over the limit (haversine ≠ driving
            // distance, plus geocoders introduce 0.2-0.5mi noise on rural
            // addresses). Beyond that, alert the user.
            if (coords.length >= 2) {
              const overLimit = maxPair > maxRadiusMiles * 1.15;
              setRadiusSpread({
                miles: maxPair,
                limit: maxRadiusMiles,
                status: overLimit ? 'over' : 'ok',
              });
              if (overLimit) {
                toast.warning(
                  `The AI couldn't keep all 3 stops inside ${maxRadiusMiles} mi — the widest pair is ${maxPair.toFixed(1)} mi apart. Try a slightly looser radius or a different region.`,
                  { duration: 7000 },
                );
              }
            }
          }

          return reordered;
        });
      }
    } catch (err) {
      setIsSearching(false);
      const status = err?.response?.status;
      // Backend returns a unified copy via AI_UNAVAILABLE_MESSAGE for any
      // cost-control gate (rate limit / daily budget / LLM failure). Honor
      // it when present so we don't override the polite phrasing.
      const serverMsg = err?.response?.data?.detail;
      if (status === 503) {
        toast.error(serverMsg || 'AI Concierge is temporarily unavailable, please try later.');
      } else if (status === 429) {
        toast.error(serverMsg || 'AI Concierge is temporarily unavailable, please try later.');
      } else {
        toast.error('Something went wrong while planning your trip. Please try again.');
      }
    }
  };

  // Mirror the conversation state into sessionStorage so the user can
  // navigate to /signin (or /checkout) and come back via the browser's
  // Back button without losing their itinerary. Persist only the
  // restorable, JSON-friendly fields — transient flags like
  // `isSearching` are intentionally excluded.
  useEffect(() => {
    persistSession({
      query,
      submittedQuery,
      results,
      commentary,
      followUps,
      matchedWinery,
      activeChatHistory,
      maxRadiusMiles,
      radiusSpread,
    });
  }, [query, submittedQuery, results, commentary, followUps, matchedWinery, activeChatHistory, maxRadiusMiles, radiusSpread]);

  const handleResetChat = () => {
    abortRef.current = true;
    setActiveChatHistory([]);
    setResults([]);
    setCommentary('');
    setFollowUps([]);
    setMatchedWinery(null);
    setSubmittedQuery('');
    setRejectedQuery(null);
    setQuery('');
    setIsSearching(false);
    setRoute(null);
    setRouteError(false);
    setRadiusSpread(null);
    routeKeyRef.current = '';
    if (typeof window !== 'undefined') {
      try { window.sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
    }
    toast.success('Trip cleared — start a fresh conversation any time.');
  };

  /**
   * Close the split-screen panel WITHOUT wiping conversation memory. The user
   * sees the default hero again, but the next query continues the same thread
   * (turn counter keeps going up). Distinct from handleResetChat.
   */
  const closePanel = () => {
    abortRef.current = true;
    setResults([]);
    setCommentary('');
    setFollowUps([]);
    setIsSearching(false);
    setSubmittedQuery('');
    setRejectedQuery(null);
    setRoute(null);
    setRouteError(false);
    routeKeyRef.current = '';
  };

  // Outlier rejection — strips coords from any stop whose median distance from
  // the others exceeds 250 mi (almost always a geocoder country-collision).
  const cleanedResults = rejectOutliers(results);
  const mapped = cleanedResults.filter(
    (r) => typeof r.lat === 'number' && typeof r.lon === 'number',
  );

  // Fetch the OSRM driving route whenever the set of geocoded stops changes.
  useEffect(() => {
    if (mapped.length < 2) {
      setRoute(null);
      setRouteError(false);
      routeKeyRef.current = '';
      return undefined;
    }
    const key = mapped.map((p) => `${p.lat.toFixed(4)},${p.lon.toFixed(4)}`).join('|');
    if (key === routeKeyRef.current) return undefined;
    routeKeyRef.current = key;

    let cancelled = false;
    const controller = new AbortController();
    (async () => {
      try {
        const coordStr = mapped.map((p) => `${p.lon},${p.lat}`).join(';');
        const res = await fetch(`${API}/ai-search/route?coords=${encodeURIComponent(coordStr)}`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`route HTTP ${res.status}`);
        const data = await res.json();
        const coords = Array.isArray(data?.geometry) ? data.geometry : null;
        if (!coords || coords.length === 0) throw new Error('no route');
        if (cancelled) return;
        setRoute({
          coords,
          distance_m: data.distance_m,
          duration_s: data.duration_s,
          legs: Array.isArray(data.legs) ? data.legs : [],
        });
        setRouteError(false);
      } catch (e) {
        if (cancelled || e?.name === 'AbortError') return;
        setRoute(null);
        setRouteError(true);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapped.map((p) => `${p.lat},${p.lon}`).join('|')]);

  /**
   * Resolve a clarification quick-pick into a concrete search.
   * Called from <AiSearchClarification /> when the user taps a chip.
   * `pick` is one entry from clarification.picks; `region` is the
   * { label, value } the user chose (or pre-filled when only one was
   * offered).
   */
  const resolveClarification = (pick, region) => {
    if (!pick || !pick.expand || !region || !region.value) return;
    const expandedQuery = pick.expand.replace('{region}', region.value);
    setClarification(null);
    setQuery(expandedQuery);
    // Pass through to the normal search path — the expanded query no
    // longer matches the qualifier (it now has a category anchor +
    // region), so it flows to cache/LLM as a regular itinerary request.
    runSearch(expandedQuery);
  };

  return {
    query,
    setQuery,
    submittedQuery,
    isSearching,
    results,
    mapped,
    commentary,
    followUps,
    matchedWinery,
    rejectedQuery,
    setRejectedQuery,
    clarification,
    setClarification,
    resolveClarification,
    activeChatHistory,
    completedTurns,
    turnCount,
    searchActive,
    runSearch,
    handleResetChat,
    closePanel,
    maxRadiusMiles,
    setMaxRadiusMiles,
    radiusSpread,
  };
}
